import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Message, MessageHeader, Button, Header, Input } from "semantic-ui-react";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { sortPartiesForUser } from "../partySlice";
import { partyIsAgent } from "../../../app/common/util/util";
import { openModal } from "../../../app/common/modals/modalSlice";
import usePartyTransactionsByStatus from "./usePartyTransactionsByStatus";
import PartyTransactionsTable from "./PartyTransactionsTable";

export default function PartyAllOverview() {
  const dispatch = useDispatch();
  const { partiesForUser } = useSelector((state) => state.party);
  const {
    loaded,
    currentUserProfile,
    parties,
    partiesForDisplay,
    underContractTransactions,
    activeTransactions,
    completedTransactions,
    archivedTransactions,
  } = usePartyTransactionsByStatus();
  const [searchTerm, setSearchTerm] = useState("");
  const column = partiesForUser?.column;
  const direction = partiesForUser?.direction;

  if (!loaded) return <LoadingComponent content="Loading details..." />;

  // Should this party-type user see the "Subscribe to TransActioner" message?
  // - Previously-subscribed agents (role === "agent") already get this message to resubscribe.
  // - Agents who have never subscribed don't have role === "agent" yet, so we need another way to
  //   confirm they're actually an agent (and not a client/lender/title/etc. who shouldn't see this):
  //     1. Automatic: they show up as an agent (Listing Agent/Buyer Agent/etc.) on one of their own
  //        party records across any transaction, via the shared partyIsAgent() role check, OR they're
  //        listed as the coAgent on someone else's agent party record.
  //     2. Manual: currentUserProfile.isAgent is set by hand for agents not yet caught by #1
  //        (e.g. not yet added to a transaction under their own email).
  const isDetectedAsAgent = parties?.some((party) => {
    const isPrimaryAgent =
      party.email === currentUserProfile?.email && partyIsAgent(party.role);
    const isCoAgent = party.coAgent?.email === currentUserProfile?.email;
    return isPrimaryAgent || isCoAgent;
  });
  const isAgentNotYetSubscribed =
    currentUserProfile?.type === "party" &&
    currentUserProfile?.role !== "agent" &&
    (isDetectedAsAgent || currentUserProfile?.isAgent === true);
  const isPreviousSubscriberAgent =
    currentUserProfile?.role === "agent" && currentUserProfile?.type === "party";
  const showSubscribeMessage = isPreviousSubscriberAgent || isAgentNotYetSubscribed;

  function handleSort(column) {
    dispatch(sortPartiesForUser(column));
  }

  const totalTransactionCount = partiesForDisplay?.length || 0;

  const filterTransactions = (transactions) => {
    if (!searchTerm.trim()) return transactions;
    const term = searchTerm.toLowerCase();
    return transactions.filter((party) => {
      const fields = [
        party.address?.street,
        party.address?.city,
        party.address?.state,
        party.address?.zipCode,
        party.agentFirstName,
        party.agentLastName,
        party.agentRepresents,
        party.title,
      ];
      return fields.some((field) =>
        field?.toString().toLowerCase().includes(term)
      );
    });
  };

  // Helper function to render a table section
  const renderTable = (transactions, title, showClosingDate = false) => (
    <PartyTransactionsTable
      transactions={filterTransactions(transactions || [])}
      title={title}
      showClosingDate={showClosingDate}
      column={column}
      direction={direction}
      onSort={handleSort}
    />
  );

  return (
    <div className="main-page-wrapper">
      <>
        <Grid stackable className="small bottom margin">
          {currentUserProfile?.firstName?.length > 1 && (
            <Grid.Column computer={16} className="small bottom margin">
              <Header size="medium">Welcome {currentUserProfile?.firstName}</Header>
            </Grid.Column>
         )}

          {showSubscribeMessage && (
          <Grid.Column computer={16} className="small bottom margin">
            <Message color="blue" className="zero margin">
              <MessageHeader>
                {/* <center>
                  <Image
                    src="/assets/logo-original-with-text-75H.png"
                    style={{ align: "center" }}
                  />
                </center>
                <br /> */}


              {isPreviousSubscriberAgent ? (
                <>
                  <a target="_blank" rel="noopener noreferrer" href="https://buy.stripe.com/9B628q4WG26geqe6cB5kk0i">Option to Subscribe to TransActioner to make use of all the features TransActioner provides! Free Trial for new users</a>
                  {/* button Subscribe */}
                  <Button
                    primary
                    floated="right"
                    onClick={() => window.open("https://buy.stripe.com/9B628q4WG26geqe6cB5kk0i", "_blank")}
                    content="Subscribe To TransActioner"
                  />
                </>
              ) : (
                <>
                  Agents! Sign up for a free trial of TransActioner to make use of all the features TransActioner provides!
                  {/* button Free Trial signup */}
                  <Button
                    primary
                    floated="right"
                    onClick={() =>
                      dispatch(
                        openModal({ modalType: "PartyFreeTrialSignupForm" })
                      )
                    }
                    content="Sign Up for Free Trial"
                  />
                </>
              )}
              </MessageHeader>
                  &nbsp;
            </Message>
          </Grid.Column>
          )}
          <Grid.Column computer={16} className="small bottom margin">
            <h3
              className="zero bottom margin"
              style={{ position: "absolute", bottom: "0" }}
            >
              Transaction List
            </h3>
          </Grid.Column>
          {totalTransactionCount >= 10 && (
            <Grid.Column computer={16} className="small bottom margin">
              <Input
                fluid
                icon="search"
                placeholder="Search by address, agent, or side..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid.Column>
          )}
          <Grid.Column computer={16} className="small bottom margin">
            {partiesForDisplay && partiesForDisplay.length !== 0 ? (
              <>
                {renderTable(underContractTransactions, "Under Contract", true)}
                {renderTable(activeTransactions, "Active Transactions")}
                {renderTable(completedTransactions, "Completed", true)}
                {renderTable(archivedTransactions, "Archived")}
              </>
            ) : (
              <Message size="large">
                If you do not see a transaction below, make sure you logged in
                using the same email where you received the notification that
                documents were shared with you.
                <br />
                <hr  />
                <b>
                You are logged in as: {currentUserProfile?.email || "[not logged in properly]"}
                </b>
                <hr />
                <br />
                Also, be sure you are not using a VPN or behind a company's firewall to access this site.
                <br /> <br />
                You can access your account from your phone as an alternative
                way to log in and sign/view documents.
                <br /> <br />
                If you are still having problems, then reach out to the agent
                who sent you a notification or contact <a href="mailto:support@transactioner.com">support@transactioner.com</a>
              </Message>
            )}
          </Grid.Column>
        </Grid>
      </>
    </div>
  );
}
